import { QueryLazyOptions } from "@apollo/client/react/types/types";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";

import { Maybe, OffsetPaginationDetails } from "../main/graphql";
import useDebounce from "./useDebounce";

export type PageOptions = {
  currentPage: number;
  totalPages: number;
  handlePageNumberChange(p: number): void;
  sortBy: SortingRule<string>;
  onChangeSort(s: SortingRule<string>[]): void;
};

type OffsetPaginationReturn<T> = {
  listItems: T[];
  setListItems(items: T[]): void;
  setNumberOfPages(n: number): void;
  pageOptions: PageOptions;
  setSearchTerm(q: string): void;
  searchTerm: string;
  didFetchComplete: boolean;
  reload: () => void;
};

function useOffsetPagination<T>(
  getData: (
    options?: QueryLazyOptions<{ pagination?: Maybe<OffsetPaginationDetails> }>
  ) => any,
  options: {
    initialSortBy: SortingRule<string>;
    initialSearchTerm?: string;
    ignoreURLParams?: boolean;
    pageLimit?: number;
    scrollToTop?: boolean;
  }
): OffsetPaginationReturn<T> {
  const {
    initialSortBy,
    initialSearchTerm,
    ignoreURLParams,
    pageLimit,
    scrollToTop,
  } = {
    initialSearchTerm: "",
    ignoreURLParams: false,
    pageLimit: 40,
    scrollToTop: true,
    ...options,
  };
  const [listItems, setListItems] = useState<T[]>([]);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [didFetchComplete, setDidFetchComplete] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [sortBy, setSortBy] = useState<SortingRule<string>>(initialSortBy);
  const [searchParams, setSearchParams] = useSearchParams();
  const debouncedSearch = useDebounce(searchTerm, 500);

  useEffect(() => {
    if (!ignoreURLParams) {
      const page = searchParams.get("page");
      const sortBy = searchParams.get("sortBy");
      const desc = searchParams.get("desc");
      const searchTerm = searchParams.get("searchTerm");
      if (page) {
        setCurrentPage(Number(page));
      }
      if (sortBy) {
        setSortBy({
          id: sortBy,
          desc: desc !== "false",
        });
      }
      if (searchTerm) {
        setSearchTerm(searchTerm);
      }
    }
  }, [ignoreURLParams]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    if (!ignoreURLParams) {
      const firstNavigation = !searchParams.has("page");
      searchParams.set("page", currentPage.toString());
      searchParams.set("sortBy", sortBy.id);
      searchParams.set("searchTerm", debouncedSearch);
      searchParams.set("desc", sortBy.desc ? "true" : "false");
      setSearchParams(searchParams, { replace: firstNavigation });
    }
  }, [currentPage, sortBy, debouncedSearch, ignoreURLParams]);

  const load = useCallback(() => {
    const sortedBy = sortBy.desc ? `${sortBy.id} desc` : `${sortBy.id} asc`;
    const limit = pageLimit || 40;
    if (debouncedSearch && debouncedSearch.length < 3) return;
    getData({
      variables: {
        pagination: {
          limit,
          page: currentPage,
          orderBy: sortedBy,
          searchTerm: debouncedSearch,
        },
      },
    });
  }, [sortBy, currentPage, debouncedSearch, pageLimit]);

  useEffect(() => {
    load();
  }, [load]);

  const onChangeSort = (sortBy: SortingRule<string>[]): void => {
    if (sortBy.length > 0) {
      setSortBy(sortBy[0]);
    }
  };

  const handlePageNumberChange = (pageNumber: number): void => {
    if (pageNumber) {
      setCurrentPage(pageNumber);
    }
  };

  const setListItemsAndScrollToTop = (listItems: T[]): void => {
    setDidFetchComplete(true);
    setListItems(listItems);
    if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const pageOptions = {
    currentPage,
    totalPages: numberOfPages,
    handlePageNumberChange,
    onChangeSort,
    sortBy,
  };
  return {
    listItems,
    setListItems: setListItemsAndScrollToTop,
    setNumberOfPages,
    pageOptions,
    setSearchTerm,
    searchTerm,
    didFetchComplete,
    reload: load,
  };
}

export default useOffsetPagination;
